import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Form, Button, Badge, Alert, Modal, Tab, Tabs } from 'react-bootstrap';
import authService from '../services/authService';

function AdminRoadmap() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [features, setFeatures] = useState([]);
  const [pendingFeatures, setPendingFeatures] = useState([]);
  const [releases, setReleases] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');

  // Edit feature modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    status: '',
    category: '',
    release_id: '',
    ship_date: '',
    admin_notes: ''
  });
  const [saving, setSaving] = useState(false);

  // New release modal
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseData, setReleaseData] = useState({
    name: '',
    description: '',
    target_date: '',
    sort_order: 0
  });

  // Edit release modal
  const [showEditReleaseModal, setShowEditReleaseModal] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [editReleaseData, setEditReleaseData] = useState({
    name: '',
    description: '',
    target_date: '',
    sort_order: 0
  });

  // Bulk add modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFeatures, setBulkFeatures] = useState([
    { title: '', description: '', status: 'planned', category: '', release_id: '' }
  ]);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    await Promise.all([
      loadPendingFeatures(),
      loadAllFeatures(),
      loadReleases()
    ]);

    setLoading(false);
  };

  const loadPendingFeatures = async () => {
    try {
      const response = await fetch('/api/roadmap/admin/pending', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingFeatures(data.features || []);
      } else if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
      }
    } catch (err) {
      setError('Error loading pending features: ' + err.message);
    }
  };

  const loadAllFeatures = async () => {
    try {
      const response = await fetch('/api/roadmap/admin/all', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFeatures(data.features || []);
      }
    } catch (err) {
      console.error('Error loading all features:', err);
    }
  };

  const loadReleases = async () => {
    try {
      const response = await fetch('/api/roadmap/releases', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReleases(data.releases || []);
      }
    } catch (err) {
      console.error('Error loading releases:', err);
    }
  };

  const handleEdit = (feature) => {
    setSelectedFeature(feature);
    setEditData({
      title: feature.title || '',
      description: feature.description || '',
      status: feature.status || 'suggested',
      category: feature.category || '',
      release_id: feature.release_id || '',
      ship_date: feature.ship_date || '',
      admin_notes: feature.admin_notes || ''
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/roadmap/admin/feature/${selectedFeature.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...editData,
          release_id: editData.release_id || null
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setSuccess('Feature updated successfully');
        await Promise.all([loadPendingFeatures(), loadAllFeatures()]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update feature');
      }
    } catch (err) {
      setError('Error updating feature: ' + err.message);
    }

    setSaving(false);
  };

  const handleQuickApprove = async (feature) => {
    try {
      const response = await fetch(`/api/roadmap/admin/feature/${feature.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          status: 'planned'
        })
      });

      if (response.ok) {
        setSuccess(`"${feature.title}" approved and added to roadmap`);
        await Promise.all([loadPendingFeatures(), loadAllFeatures()]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to approve feature');
      }
    } catch (err) {
      setError('Error approving feature: ' + err.message);
    }
  };

  const handleQuickReject = async (feature) => {
    if (!window.confirm(`Reject "${feature.title}"? This will permanently remove the suggestion.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/roadmap/admin/feature/${feature.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          status: 'rejected'
        })
      });

      if (response.ok) {
        setSuccess(`"${feature.title}" rejected`);
        await Promise.all([loadPendingFeatures(), loadAllFeatures()]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to reject feature');
      }
    } catch (err) {
      setError('Error rejecting feature: ' + err.message);
    }
  };

  const handleCreateRelease = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/roadmap/admin/releases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(releaseData)
      });

      if (response.ok) {
        setShowReleaseModal(false);
        setReleaseData({ name: '', description: '', target_date: '', sort_order: 0 });
        setSuccess('Release created successfully');
        await loadReleases();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create release');
      }
    } catch (err) {
      setError('Error creating release: ' + err.message);
    }

    setSaving(false);
  };

  const handleEditRelease = (release) => {
    setSelectedRelease(release);
    setEditReleaseData({
      name: release.name || '',
      description: release.description || '',
      target_date: release.target_date || '',
      sort_order: release.sort_order || 0
    });
    setShowEditReleaseModal(true);
  };

  const handleSaveRelease = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch(`/api/roadmap/admin/releases/${selectedRelease.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editReleaseData)
      });

      if (response.ok) {
        setShowEditReleaseModal(false);
        setSuccess('Release updated successfully');
        await loadReleases();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update release');
      }
    } catch (err) {
      setError('Error updating release: ' + err.message);
    }

    setSaving(false);
  };

  const handleAddBulkRow = () => {
    setBulkFeatures([
      ...bulkFeatures,
      { title: '', description: '', status: 'planned', category: '', release_id: '' }
    ]);
  };

  const handleRemoveBulkRow = (index) => {
    if (bulkFeatures.length > 1) {
      setBulkFeatures(bulkFeatures.filter((_, i) => i !== index));
    }
  };

  const handleBulkFeatureChange = (index, field, value) => {
    const updated = [...bulkFeatures];
    updated[index][field] = value;
    setBulkFeatures(updated);
  };

  const handleBulkAdd = async () => {
    // Filter out empty rows
    const validFeatures = bulkFeatures.filter(f => f.title.trim() && f.description.trim());

    if (validFeatures.length === 0) {
      setError('Please add at least one feature with title and description');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/roadmap/admin/features/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          features: validFeatures.map(f => ({
            ...f,
            release_id: f.release_id || null
          }))
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowBulkModal(false);
        setBulkFeatures([{ title: '', description: '', status: 'planned', category: '', release_id: '' }]);
        setSuccess(data.message || `Added ${data.added?.length || 0} features`);
        await Promise.all([loadPendingFeatures(), loadAllFeatures()]);
      } else {
        setError(data.error || 'Failed to add features');
      }
    } catch (err) {
      setError('Error adding features: ' + err.message);
    }

    setSaving(false);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'suggested': 'secondary',
      'planned': 'primary',
      'in_progress': 'warning',
      'shipped': 'success',
      'rejected': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h1>Roadmap Management</h1>
            <div>
              <Button variant="outline-secondary" className="me-2" onClick={() => navigate('/admin')}>
                Back to Admin
              </Button>
              <Button variant="success" className="me-2" onClick={() => setShowBulkModal(true)}>
                + Add Features
              </Button>
              <Button variant="primary" onClick={() => setShowReleaseModal(true)}>
                + New Release
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="pending" title={`Pending Review (${pendingFeatures.length})`}>
          <Card>
            <Card.Body>
              {pendingFeatures.length === 0 ? (
                <p className="text-muted text-center my-4">No pending feature suggestions</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Submitted By</th>
                      <th>Votes</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingFeatures.map(feature => (
                      <tr key={feature.id}>
                        <td><strong>{feature.title}</strong></td>
                        <td style={{ maxWidth: '300px' }}>
                          {feature.description.length > 100
                            ? feature.description.substring(0, 100) + '...'
                            : feature.description}
                        </td>
                        <td>
                          {feature.submitted_by_name}<br />
                          <small className="text-muted">{feature.submitted_by_email}</small>
                        </td>
                        <td>{feature.votes_count || 0}</td>
                        <td>{feature.created_at ? new Date(feature.created_at).toLocaleDateString() : '-'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="success"
                            className="me-1"
                            onClick={() => handleQuickApprove(feature)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => handleEdit(feature)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleQuickReject(feature)}
                          >
                            Reject
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="all" title="All Features">
          <Card>
            <Card.Body>
              {features.length === 0 ? (
                <p className="text-muted text-center my-4">No features yet</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Category</th>
                      <th>Release</th>
                      <th>Votes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map(feature => (
                      <tr key={feature.id}>
                        <td><strong>{feature.title}</strong></td>
                        <td>{getStatusBadge(feature.status)}</td>
                        <td>{feature.category || '-'}</td>
                        <td>{feature.release_name || '-'}</td>
                        <td>{feature.votes_count || 0}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEdit(feature)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="releases" title="Releases">
          <Card>
            <Card.Body>
              {releases.length === 0 ? (
                <p className="text-muted text-center my-4">No releases created yet</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Target Date</th>
                      <th>Sort Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releases.map(release => (
                      <tr key={release.id}>
                        <td><strong>{release.name}</strong></td>
                        <td>{release.description || '-'}</td>
                        <td>{release.target_date || '-'}</td>
                        <td>{release.sort_order}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEditRelease(release)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Edit Feature Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Feature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  >
                    <option value="suggested">Suggested</option>
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="shipped">Shipped</option>
                    <option value="rejected">Rejected</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    type="text"
                    value={editData.category}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                    placeholder="e.g., AI Features, UX, Analytics"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Release</Form.Label>
                  <Form.Select
                    value={editData.release_id}
                    onChange={(e) => setEditData({ ...editData, release_id: e.target.value })}
                  >
                    <option value="">No Release</option>
                    {releases.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ship Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editData.ship_date}
                    onChange={(e) => setEditData({ ...editData, ship_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Admin Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editData.admin_notes}
                onChange={(e) => setEditData({ ...editData, admin_notes: e.target.value })}
                placeholder="Internal notes (not shown publicly)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Release Modal */}
      <Modal show={showReleaseModal} onHide={() => setShowReleaseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Release</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Release Name</Form.Label>
              <Form.Control
                type="text"
                value={releaseData.name}
                onChange={(e) => setReleaseData({ ...releaseData, name: e.target.value })}
                placeholder="e.g., Q1 2026, Version 2.0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={releaseData.description}
                onChange={(e) => setReleaseData({ ...releaseData, description: e.target.value })}
                placeholder="Optional description"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={releaseData.target_date}
                    onChange={(e) => setReleaseData({ ...releaseData, target_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={releaseData.sort_order}
                    onChange={(e) => setReleaseData({ ...releaseData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReleaseModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateRelease} disabled={saving || !releaseData.name}>
            {saving ? 'Creating...' : 'Create Release'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Release Modal */}
      <Modal show={showEditReleaseModal} onHide={() => setShowEditReleaseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Release</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Release Name</Form.Label>
              <Form.Control
                type="text"
                value={editReleaseData.name}
                onChange={(e) => setEditReleaseData({ ...editReleaseData, name: e.target.value })}
                placeholder="e.g., Q1 2026, Version 2.0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editReleaseData.description}
                onChange={(e) => setEditReleaseData({ ...editReleaseData, description: e.target.value })}
                placeholder="Optional description"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editReleaseData.target_date}
                    onChange={(e) => setEditReleaseData({ ...editReleaseData, target_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={editReleaseData.sort_order}
                    onChange={(e) => setEditReleaseData({ ...editReleaseData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditReleaseModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveRelease} disabled={saving || !editReleaseData.name}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Add Features Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Add Features to Roadmap</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Add features directly to the roadmap. These will be immediately visible (no approval needed).
          </p>

          {bulkFeatures.map((feature, index) => (
            <Card key={index} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Feature {index + 1}</strong>
                  {bulkFeatures.length > 1 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveBulkRow(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Title *</Form.Label>
                      <Form.Control
                        type="text"
                        value={feature.title}
                        onChange={(e) => handleBulkFeatureChange(index, 'title', e.target.value)}
                        placeholder="Feature title"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-2">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={feature.status}
                        onChange={(e) => handleBulkFeatureChange(index, 'status', e.target.value)}
                      >
                        <option value="planned">Planned</option>
                        <option value="in_progress">In Progress</option>
                        <option value="shipped">Shipped</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-2">
                      <Form.Label>Category</Form.Label>
                      <Form.Control
                        type="text"
                        value={feature.category}
                        onChange={(e) => handleBulkFeatureChange(index, 'category', e.target.value)}
                        placeholder="e.g., AI, UX"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={9}>
                    <Form.Group className="mb-2">
                      <Form.Label>Description *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={feature.description}
                        onChange={(e) => handleBulkFeatureChange(index, 'description', e.target.value)}
                        placeholder="Describe the feature..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-2">
                      <Form.Label>Release</Form.Label>
                      <Form.Select
                        value={feature.release_id}
                        onChange={(e) => handleBulkFeatureChange(index, 'release_id', e.target.value)}
                      >
                        <option value="">No Release</option>
                        {releases.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}

          <Button variant="outline-primary" onClick={handleAddBulkRow}>
            + Add Another Feature
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleBulkAdd} disabled={saving}>
            {saving ? 'Adding...' : `Add ${bulkFeatures.filter(f => f.title && f.description).length} Feature(s)`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AdminRoadmap;
